import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useAppSelector } from "hooks/storeHooks"
import { useDebounce } from "hooks/useDebounce"
import { useAlert } from "providers/AlertProvider"
import { tintVTOService } from "services/tintvto"
import { selectUserInfo } from "store/user/userSlice"
import { IntegrationType, ProductV2 } from "types"
import { parseQuery } from "utils/parseQuery"
import { typedEnv } from "../../../config/typedEnv"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Flex,
  LinkButton,
  Loader,
  Searchbar,
  SearchForm,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Typography,
} from "@strapi/design-system"
import { Eye, Plus } from "@strapi/icons"
import ConfirmDialog from "components/ConfirmDialog"
import EmptyList from "components/EmptyList"
import PageLoader from "components/PageLoader"
import Paginator from "components/Paginator"
import { ProductPreviewOverlay } from "components/ProductPreviewOverlay/ProductPreviewOverlay"
import { AddProductsModal } from "../AddProductsModal"
import { ProductTableRow } from "./ProductTableRow"
import { ProductCategories } from "components/Navbar/types"
import {
  ProductsQueryParams,
  useDeleteProductMutation,
  useProductsQuery,
  useToggleProductVisibilityMutation,
} from "hooks/query/useProductsQuery"
import { LeaveOpinionFeatureModal } from "@/features/leaveOpition/components/LeaveOpinionFeatureModal"
import { ActivatedOpinionPopup } from "@/features/leaveOpition/LeaveOpinionFeatureService"

export const ProductsPage = ({ category }: { category: ProductCategories }) => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const userInfo = useAppSelector(selectUserInfo)
  const { t } = useTranslation()
  const location = useLocation()

  const [showAddProductsModal, setShowAddProductsModal] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [productPreview, setProductPreview] = useState<ProductV2 | null>(null)
  const [productToDelete, setProductToDelete] = useState<ProductV2 | null>(null)

  const debouncedSearchText = useDebounce<string>(searchText, 300)
  const currentPage = useMemo(
    () => parseInt(parseQuery(location.search).page) || 1,
    [location.search]
  )
  const productsQueryParams: ProductsQueryParams = {
    page: currentPage,
    searchText: debouncedSearchText,
    category,
    merchantId: userInfo.merchantId ?? "",
  }
  const { data: productsResponse, isLoading } = useProductsQuery(productsQueryParams)

  const { pageCount, items: products } = productsResponse || { pageCount: 0, items: [] }

  const handleAddProduct = useCallback(() => {
    if (category === ProductCategories.GLASSES && userInfo.integrationType === IntegrationType.COMMON)
      navigate(`/glasses/add`)
    else setShowAddProductsModal(true)
  }, [category, userInfo.integrationType, navigate])

  const { mutate: toggleVisibilityHandler } =
    useToggleProductVisibilityMutation(productsQueryParams)

  const { mutate: handleDeleteProduct } = useDeleteProductMutation(productsQueryParams)

  const handlePreview = useCallback(
    async (productId: string) => {
      try {
        const product = await tintVTOService.productByIdV2(productId)
        setProductPreview(product)
      } catch {
        showAlert({ error: true, text: t("productsPage.previewError") })
      }
    },
    [showAlert, t]
  )

  const hasProductsOrSearch = products.length > 0 || debouncedSearchText.length > 0 || isLoading
  const showEmptyState =
    products.length === 0 && debouncedSearchText.length === 0 && !isLoading

  useEffect(() => {
    if (products.length === 0 && currentPage > 1 && !isLoading) navigate(`?page=${currentPage - 1}`)
  }, [currentPage, products, isLoading, navigate])

  return (
    <Box>
      <LeaveOpinionFeatureModal type={ActivatedOpinionPopup.OPINION_AFTER_INITIAL_SETUP} />
      <ConfirmDialog
        isOpen={!!productToDelete}
        onClose={() => setProductToDelete(null)}
        onConfirm={async () => {
          if (productToDelete) {
            handleDeleteProduct(productToDelete)
            setProductToDelete(null)
          }
        }}
        text={t("productsPage.deleteConfirmation", { productName: productToDelete?.name })}
      />
      {showAddProductsModal && (
        <AddProductsModal
          category={category}
          onAlert={showAlert}
          isOpen={showAddProductsModal}
          onClose={() => setShowAddProductsModal(false)}
        />
      )}
      {productPreview && (
        <ProductPreviewOverlay
          effectType={category}
          productPreview={productPreview}
          onClose={() => setProductPreview(null)}
        />
      )}
      <BaseHeaderLayout
        title={t("productsPage.title")}
        as="h1"
        primaryAction={
          products.length !== 0 && (
            <Flex gap={2} alignItems="center">
              <LinkButton
                startIcon={<Eye />}
                variant="tertiary"
                href={`https://${typedEnv.VITE_TINT_WIDGET_LINK}/?merchantId=${userInfo.merchantId}`}
              >
                {t("previewTintVto")}
              </LinkButton>
              <Button startIcon={<Plus />} onClick={handleAddProduct}>
                {userInfo.integrationType === IntegrationType.TIENDANUBE
                  ? t("productsPage.selectSKU")
                  : t("productsPage.addProduct")}
              </Button>
            </Flex>
          )
        }
        subtitle={
          products.length === 0
            ? t("productsPage.zeroProductsSubtitle")
            : t("productsPage.subtitle", {
                count: products.length < 30 ? products.length : 30 * pageCount,
              })
        }
      />
      <ContentLayout>
        <SearchForm className="hover:brightness-120">
          <Searchbar
            name="searchbar"
            disabled={!products.length && searchText === ""}
            onClear={() => setSearchText("")}
            value={searchText}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchText(e.target.value)
            }}
            clearLabel={t("productsPage.clear")}
            placeholder={t("productsPage.searchPlaceholder")}
          />
        </SearchForm>
        <Box padding={2} />
        {hasProductsOrSearch && !isLoading ? (
          <>
            {products.length > 0 ? (
              <>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>{t("productsPage.logo")}</Th>
                      <Th>{t("productsPage.brand")}</Th>
                      <Th>{t("productsPage.name")}</Th>
                      <Th>{t("productsPage.type")}</Th>
                      {category === ProductCategories.MAKEUP && (
                        <Th style={{ maxWidth: "132px" }}>{t("productsPage.colors")}</Th>
                      )}
                      <Th style={{ minWidth: "200px" }}>{t("productsPage.visibility")}</Th>
                      <Th style={{ width: "150px" }}>{t("productsPage.actions")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {products.map((product) => (
                      <ProductTableRow
                        key={product.id}
                        product={product}
                        setProductDelete={setProductToDelete}
                        toggleVisibilityHandler={toggleVisibilityHandler}
                        onPreview={handlePreview}
                      />
                    ))}
                  </Tbody>
                </Table>
                <Box padding={2} />
                <Paginator page={currentPage} pageCount={pageCount} />
              </>
            ) : (
              <Box padding={4}>
                <Typography textColor="neutral600" textAlign="center">
                  {t("productsPage.emptyText")}
                </Typography>
              </Box>
            )}
          </>
        ) : null}
      </ContentLayout>
      {showEmptyState && (
        <Box style={{ position: "absolute", top: "45%", left: "45%" }}>
          <EmptyList
            text={t("productsPage.emptyText")}
            addText={
              userInfo.integrationType === IntegrationType.TIENDANUBE
                ? t("productsPage.selectSKU")
                : t("productsPage.addProduct")
            }
            onClick={handleAddProduct}
            showAdd={debouncedSearchText === ""}
          />
        </Box>
      )}
      {isLoading && <PageLoader />}
    </Box>
  )
}
